import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import tPaths from '../../../utils/tPaths';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import useStyles from './ReportGenerator.styles';
import { endpoints } from '../../../utils/endpoints';
import useApi from '../../../utils/useApi';
import { downloadFile } from '../../../utils/downloadUtil';
import { ETables } from '../../../types/enums/ETables';
import { DateRange, Range } from 'react-date-range';
import { themeColors } from '../../../styles/themeColors';
import { IDatePickerProps } from '../../../components/DatePicker/DatePicker';

const ReportGenerator = () => {
  const { get } = useApi(null);
  const classes = useStyles();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(ETables.Customers);

  const currentDate = new Date();
  const sixMonthsAgo = new Date(currentDate.setMonth(currentDate.getMonth() - 6));
  const [dateRange, setDateRange] = useState({ startDate: sixMonthsAgo, endDate: new Date() });

  const title = t(tPaths.reportGenerator.generate);

  const selectData: IEditForm = useMemo(
    () => ({
      fieldName: t(tPaths.reportGenerator.types),
      onChange: (_, value) => setSelectedType(value),
      label: <Typography className={classes.selectLabel} children={t(tPaths.reportGenerator.selectReportType)} />,
      value: selectedType,
      initialValue: ETables.Customers,
      fullWidth: true,
      type: 'select',
      variants: [
        { value: ETables.Customers, label: t(tPaths.sideMenu.customers) },
        { value: ETables.PurchasedPackages, label: t(tPaths.reportGenerator.purchasedPackages) },
      ],
    }),
    [t, selectedType],
  );

  const selectionRange = useMemo(
    () => ({
      ...dateRange,
      key: 'selectedRange',
    }),
    [dateRange, setDateRange],
  );

  const handleRangeChange = ({ selectedRange }: { selectedRange: IDatePickerProps['range'] }) => { setDateRange(selectedRange); };

  const closePopup = () => setIsOpen(false);

  const handleGenerateClick = useCallback(
    () =>
      get({
        endpoint: endpoints.exportReport({ tableName: selectedType, dateFrom: dateRange.startDate, dateTo: dateRange.endDate }),
        onSuccess: ({ data }) => downloadFile(data, { name: selectedType, extension: 'xlsx' }),
        options: { responseType: 'blob' },
      }),
    [get, selectedType, dateRange],
  );

  return (
    <>
      <Dialog open={isOpen} fullWidth={true} onBackdropClick={closePopup}>
        <DialogTitle>
          <Typography className={classes.title} children={title} />
        </DialogTitle>

        <DialogContent>
          <DateRange
            weekStartsOn={1}
            ranges={[selectionRange]}
            // @ts-ignore error in library types
            onChange={handleRangeChange}
            rangeColors={[themeColors.selected, themeColors.primary]}
            className={classes.dateRange}
          />
          <Box pt={2}>
            <CustomSelect {...selectData} />
          </Box>
        </DialogContent>

        <DialogActions className={classes.actionsContainer}>
          <Button className={classes.button} onClick={closePopup} children={t(tPaths.global.cancel)} />
          <Button className={classes.button} color="secondary" children={title} onClick={handleGenerateClick} />
        </DialogActions>
      </Dialog>

      <Box marginLeft={1}>
        <Button onClick={() => setIsOpen(true)} children={t(tPaths.table.downloadXls)} />
      </Box>
    </>
  );
};

export default ReportGenerator;
